<template>
  <div class="quiz-article">
    <div class="quiz-article__wrapper">
      <div class="quiz-article__analytics">
        <AppHtml
          v-if="quiz"
          :elem="quiz"
        />
      </div>
      <div class="quiz-article__content-wrapper">
        <template
          v-for="(contentItem, index) in slicedContents"
          :key="index"
        >
          <TheContent
            v-for="item of contentItem"
            :key="item.id"
            :elem="item"
          />
          <WidgetsArea
            v-if="index === 0"
            area="after-first-paragraph"
          />
          <!-- виджет не должен быть показан в конце, если это последний чанк -->
          <WidgetsArea
            v-else-if="index !== slicedContents.length - 1"
            :area="'post-dynamic'"
            :index-sliced-content="index"
          />
        </template>
      </div>
    </div>
    <div class="quiz-article__content quiz-article__content--article-footer">
      <AuthorsBlock
        v-if="article.author"
        class="quiz-article__authors-block"
      />
      <ArticleFooter class="quiz-article__footer" />
    </div>
    <div class="quiz-article__content quiz-article__content--bottom">
      <WidgetsArea area="above-comments" />

      <!-- комментарии -->
      <CommentsContainer
        v-if="showComments"
        :id="article.id"
        class="comments"
      />

      <WidgetsArea area="below-comments" />
    </div>
  </div>
</template>

<script lang="ts">
  import type { IFullArticle, TContent } from '~/types/common';
  import AuthorsBlock from '~/components/AuthorsBlock.vue';
  import ArticleFooter from '~/components/ArticleFooter.vue';
  import CommentsContainer from '~/components/CommentsContainer.vue';
  import AppHtml from '~/components/AppHtml.vue';
  import { checkQuiz } from '~/utils/wideTemplate';
  import { useSingleArticleStore } from '~/store/singleArticle';
  import WidgetsArea from '~/components/WidgetsArea/WidgetsArea.vue';
  import TheContent from '~/components/TheContent';
  import { getSplicedContent } from '@devhacker/shared/utils/adfox';

  export default defineNuxtComponent({
    name: 'QuizArticle',

    components: {
      TheContent,
      WidgetsArea,
      AuthorsBlock,
      ArticleFooter,
      CommentsContainer,
      AppHtml,
    },

    computed: {
      slicedContents(): TContent[][] {
        const spliced = getSplicedContent(this.content, 'bh-kiss__paragraph', 7);
        return spliced;
      },

      article() {
        const article = useSingleArticleStore().article;

        if (!article) {
          throw createError({ status: 500, message: `Article hasn't been provided from pinia` });
        }

        return article as IFullArticle;
      },

      showComments() {
        return this.article?.comments_enabled;
      },

      content() {
        if (this.quiz) {
          return this.article?.parsed_content.slice(1);
        }

        return this.article?.parsed_content;
      },

      quiz(): TContent | null {
        if (!this.article?.parsed_content.length) {
          return null;
        }

        const quiz = this.article.parsed_content[0];

        return checkQuiz(quiz) ? quiz : null;
      },
    },
  });
</script>

<style lang="scss" scoped>
  @use '~/scss/article' as *;

  .quiz-article {
    background-color: black;
    height: 100%;

    @include tablet {
      margin-top: 0;
      background-color: #ffffff;
    }

    &__wrapper {
      background-color: #ffffff;
      border-radius: 32px 32px 0 0;
      padding-bottom: $spacing-8;
      margin-top: $spacing-1;
      overflow: hidden;

      @include tablet {
        margin-top: 0;
        padding-bottom: $spacing-10;
        background-color: none;
      }
    }

    &__content {
      background: #fff;
      margin: 0 auto;
      box-sizing: content-box;
      padding-left: $spacing-4;
      padding-right: $spacing-4;

      @include tablet {
        max-width: 592px;
        padding: 0;
      }

      @include big-tablet {
        max-width: 732px;
      }

      &--bottom {
        padding-top: $spacing-8;
        background: #fff;

        @include tablet {
          padding-top: $spacing-10;
        }
      }

      &--article-footer {
        padding: $spacing-12 auto 0;

        @include tablet {
          padding: 0;
          max-width: 592px;
        }

        @include big-tablet {
          max-width: 732px;
        }
      }
    }

    &__content-wrapper {
      padding-left: $spacing-4;
      padding-right: $spacing-4;
      margin: 0 auto;
      box-sizing: content-box;

      @include tablet {
        padding: 0;
        max-width: 592px;
      }

      @include big-tablet {
        max-width: 732px;
      }

      @include article-picture-styles;
    }

    &__content-wrapper :deep() > {
      @include article-elems;
    }

    &__footer {
      margin-top: $spacing-6;
    }
  }

  .comments {
    padding: 0 0;
    margin-top: 0 !important;
    margin-left: auto !important;
    margin-right: auto !important;

    &:not(:last-child) {
      margin-bottom: $spacing-8 !important;
      @include tablet {
        margin-bottom: $spacing-10 !important;
      }
    }

    @include tablet {
      padding: 0;
    }

    @include big-tablet {
      padding: 0;
    }

    :deep() .lh-talker-app {
      margin-top: 0;
      margin-bottom: 0;

      .form {
        > *:first-child {
          border: 2px solid $stroke-color--1;

          @include hover {
            border-color: #699bdc;
          }
        }
      }
    }
  }
</style>
